import { useEffect, useMemo, useRef, useState } from 'react';
import { grovedb } from '@willow/sdk';
import { InfoToggle } from '../components/InfoToggle';
import { useVerifyPrefill } from '../lib/verify-prefill';

const { bytesToHex } = grovedb;

export function ProofViewerPanel() {
  const { structuredProof, setStructuredProof } = useVerifyPrefill();
  const [proofJson, setProofJson] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (structuredProof) {
      setProofJson(JSON.stringify(structuredProof, null, 2));
      setStructuredProof(null);
    }
  }, [structuredProof, setStructuredProof]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const proof = useMemo(() => parseJson(proofJson), [proofJson]);
  const stateRoot = proof?.state_root;
  const blockHeight = proof?.block_height;
  const merkleProofs = proof?.merkle_proofs;

  return (
    <section className="panel">
      <h2>
        Proof viewer
        <InfoToggle>
          Displays structured Merkle proofs returned by GraphQL and SQL query endpoints. Each
          proof contains a state root, block height, and one or more Merkle path proofs linking
          the queried data to the authenticated GroveDB root.
        </InfoToggle>
      </h2>

      {proofJson ? (
        <div className="result">
          <div className="result-header">
            <span className="badge badge-ok">structured proof</span>
            <div className="result-header-right">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(proofJson);
                  setCopied(true);
                  if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
                  copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? 'copied' : 'copy JSON'}
              </button>
            </div>
          </div>

          <dl className="kv-list">
            <dt>state root</dt>
            <dd>
              <code>{Array.isArray(stateRoot) ? bytesToHex(new Uint8Array(stateRoot)) : '—'}</code>
            </dd>
            <dt>block height</dt>
            <dd>{blockHeight ?? '—'}</dd>
            <dt>merkle proofs</dt>
            <dd>{Array.isArray(merkleProofs) ? merkleProofs.length : 0}</dd>
          </dl>

          {Array.isArray(merkleProofs) && merkleProofs.length > 0 && (
            <>
              <h3>Merkle proofs</h3>
              {merkleProofs.map((mp: { key?: string } & Record<string, unknown>, i: number) => (
                <details key={i} className="collapsible" style={{ marginTop: '0.5rem' }}>
                  <summary className="collapsible-header">
                    Proof {i + 1}: <code>{mp.key ?? `path ${i}`}</code>
                  </summary>
                  <div className="collapsible-body">
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                      {JSON.stringify(mp, null, 2)}
                    </pre>
                  </div>
                </details>
              ))}
            </>
          )}

          <details className="collapsible" style={{ marginTop: '0.75rem' }}>
            <summary className="collapsible-header">Full proof JSON</summary>
            <div className="collapsible-body">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>{proofJson}</pre>
            </div>
          </details>
        </div>
      ) : (
        <p className="subtle">
          No proof loaded. Click "Open in Verify" from a SQL, GraphQL, or Analytics query result to
          inspect its proof here.
        </p>
      )}
    </section>
  );
}

interface ParsedProof {
  state_root?: number[];
  block_height?: number;
  merkle_proofs?: Array<{ key?: string } & Record<string, unknown>>;
}

function parseJson(text: string): ParsedProof | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as ParsedProof;
  } catch {
    return null;
  }
}
